import type { InstructorEntry, InstructorListResponse, InviteInstructorResponse } from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { MobileList, MobileRow, Sheet, SHEET_DANGER } from '../../components/MobileList';
import { PLAYER_ROW, PlayerTable, QUIET_ACTION, Td, Th, ThActions } from '../../components/PlayerTable';
import { Button, ErrorNote, Field, Input } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import { InviteButton } from '../players/SignInInvite';

/**
 * A DIRECTOR'S INSTRUCTORS (owner, 2026-10-16): the people who run the director's session nights.
 * What an instructor can then see is decided on the server — this page only says who, and which
 * leagues.
 *
 * The table only reads (owner, 2026-10-17, option A): leagues as text, one Edit per row. Edit — or
 * tapping the row on a phone — opens one panel with a checklist of leagues, Save and Cancel, and
 * Remove. A row of toggle chips in the table was rejected as a jumble of buttons.
 */
export function InstructorsPage() {
  const queryClient = useQueryClient();
  const data = useQuery({
    queryKey: ['instructors'],
    queryFn: () => apiFetch<InstructorListResponse>('/api/director/instructors'),
  });
  const [inviting, setInviting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [removing, setRemoving] = useState<InstructorEntry | null>(null);
  const confirm = useRef<HTMLDialogElement>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['instructors'] });

  const setLeagues = useMutation({
    mutationFn: ({ id, leagueIds }: { id: string; leagueIds: string[] }) =>
      apiFetch<InviteInstructorResponse>(`/api/director/instructors/${id}/leagues`, { method: 'PUT', body: JSON.stringify({ leagueIds }) }),
    onSuccess: async () => {
      await refresh();
      setOpenId(null);
    },
  });
  /* Resend invite (2026-10-20): as the roster's, for an instructor who has not signed in yet. */
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const reinvite = useMutation({
    mutationFn: (instructor: InstructorEntry) => apiPost<{ sent: boolean }>(`/api/director/instructors/${instructor.id}/invitation`, {}),
    onMutate: () => setInviteNote(null),
    onSuccess: (_result, instructor) => setInviteNote(`Sent ${instructor.name} a new invitation.`),
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/director/instructors/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      confirm.current?.close();
      setRemoving(null);
      setOpenId(null);
      await refresh();
    },
  });

  if (data.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
  /* Refused is not broken: an instructor who reaches this page is told why (2026-10-18). */
  if (data.error instanceof ApiError && data.error.status === 403) {
    return <div className="px-6 py-10"><ErrorNote>Only a director can manage instructors. You’re signed in with an instructor’s login.</ErrorNote></div>;
  }
  if (data.isError || !data.data) return <div className="px-6 py-10"><ErrorNote>Could not load your instructors.</ErrorNote></div>;

  const { instructors, leagues } = data.data;
  const open = (instructor: InstructorEntry) => {
    setLeagues.reset();
    setDraft(instructor.leagueIds);
    setOpenId(instructor.id);
  };
  const leagueNames = (instructor: InstructorEntry) =>
    leagues.filter((l) => instructor.leagueIds.includes(l.id)).map((l) => l.name);
  const askToRemove = (instructor: InstructorEntry) => {
    remove.reset();
    setRemoving(instructor);
    confirm.current?.showModal();
  };
  const opened = instructors.find((i) => i.id === openId) ?? null;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5 px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-[34px] leading-tight">Instructors</h1>
        {inviting ? null : <Button onClick={() => setInviting(true)}>Invite an instructor</Button>}
      </div>

      {inviteNote ? <p role="status" className="text-[13px] text-ink-2">{inviteNote}</p> : null}
      {reinvite.isError ? <ErrorNote>{reinvite.error instanceof ApiError ? reinvite.error.message : 'Could not send the invitation.'}</ErrorNote> : null}

      {inviting ? <InviteForm onDone={() => { setInviting(false); void refresh(); }} onCancel={() => setInviting(false)} /> : null}

      {instructors.length === 0 && !inviting ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[14px] text-ink-3">No instructors yet.</p>
      ) : null}

      {instructors.length > 0 ? (
        <>
          <PlayerTable head={<><Th>Instructor</Th><Th>Status</Th><Th>Leagues</Th><ThActions /></>}>
            {instructors.map((instructor) => (
              <tr key={instructor.id} className={`${PLAYER_ROW} hover:bg-chalk`}>
                <Td kind="name" className="py-3">
                  <span className="block text-[16px] font-semibold text-ink">{instructor.name}</span>
                  <span className="block text-[13px] text-ink-2">{instructor.email}</span>
                </Td>
                <Td className="whitespace-nowrap text-[14px] text-ink-2">{instructor.status === 'invited' ? 'Invited' : 'Active'}</Td>
                <Td className="py-3 text-[14px]">
                  {leagueNames(instructor).length ? leagueNames(instructor).join(', ') : <span className="text-ink-3">None yet</span>}
                </Td>
                <Td kind="actions">
                  <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                    <InviteButton player={nameParts(instructor)} account={instructor.status === 'invited' ? 'invited' : 'signed_in'} disabled={reinvite.isPending} onInvite={() => reinvite.mutate(instructor)} />
                    <button type="button" className={QUIET_ACTION} aria-label={`Edit ${instructor.name}`} onClick={() => open(instructor)}>
                      Edit
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </PlayerTable>

          <MobileList>
            {instructors.map((instructor) => {
              const [first = '', ...rest] = instructor.name.split(' ');
              const runs = leagueNames(instructor);
              return (
                <MobileRow
                  key={instructor.id}
                  firstName={first}
                  lastName={rest.join(' ')}
                  onOpen={() => open(instructor)}
                  lines={[
                    instructor.status === 'invited' ? 'Invited' : 'Active',
                    runs.length === 0 ? <span className="text-ink-3">No leagues yet</span> : runs.length === 1 ? runs[0] : `${runs.length} leagues`,
                  ]}
                />
              );
            })}
          </MobileList>
        </>
      ) : null}

      {opened ? (
        <Sheet
          open
          onClose={() => setOpenId(null)}
          title={opened.name}
          subtitle={`${opened.email} · ${opened.status === 'invited' ? 'Invited' : 'Active'}`}
          footer={<>
            <div className="flex gap-3">
              <Button disabled={setLeagues.isPending} onClick={() => setLeagues.mutate({ id: opened.id, leagueIds: draft })}>
                {setLeagues.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="secondary" disabled={setLeagues.isPending} onClick={() => setOpenId(null)}>Cancel</Button>
            </div>
            <button type="button" className={SHEET_DANGER} onClick={() => askToRemove(opened)}>Remove instructor</button>
          </>}
        >
          {opened.status === 'invited' ? (
            <div className="-ml-2 mb-3">
              <InviteButton player={nameParts(opened)} account="invited" disabled={reinvite.isPending} onInvite={() => reinvite.mutate(opened)} />
              {inviteNote ? <p role="status" className="ml-2 text-[13px] text-ink-2">{inviteNote}</p> : null}
            </div>
          ) : null}
          <fieldset>
            <legend className="mb-1 text-[13px] text-ink-3">Leagues</legend>
            {leagues.length === 0 ? (
              <p className="py-2 text-[15px] text-ink-3">No current leagues</p>
            ) : leagues.map((league) => (
              <label key={league.id} className="flex min-h-12 cursor-pointer items-center gap-3 border-b border-light text-[15px] last:border-b-0">
                <input
                  type="checkbox"
                  className="size-5 accent-deep"
                  checked={draft.includes(league.id)}
                  onChange={(event) => setDraft((d) => event.target.checked ? [...d, league.id] : d.filter((id) => id !== league.id))}
                />
                {league.name}
              </label>
            ))}
          </fieldset>
          {setLeagues.error ? <ErrorNote>{setLeagues.error instanceof ApiError ? setLeagues.error.message : 'Could not change their leagues.'}</ErrorNote> : null}
        </Sheet>
      ) : null}

      <dialog
        ref={confirm}
        aria-labelledby="remove-instructor-title"
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-md border border-light bg-paper p-6 text-ink backdrop:bg-black/30"
        onCancel={(event) => { if (remove.isPending) event.preventDefault(); else setRemoving(null); }}
      >
        <h2 id="remove-instructor-title" className="font-display text-xl">Remove {removing?.name}?</h2>
        <p className="mt-2 text-sm text-ink-2">They will no longer be able to run your leagues.</p>
        {remove.error ? <ErrorNote>{remove.error instanceof ApiError ? remove.error.message : 'Could not remove them.'}</ErrorNote> : null}
        <div className="mt-5 flex gap-3">
          <Button autoFocus variant="secondary" disabled={remove.isPending} onClick={() => { confirm.current?.close(); setRemoving(null); }}>Cancel</Button>
          <Button variant="destructive" disabled={remove.isPending || !removing} onClick={() => removing && remove.mutate(removing.id)}>
            {remove.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </dialog>
    </div>
  );
}

/** The roster's InviteButton names a person by first and last name. */
function nameParts(instructor: InstructorEntry): { firstName: string; lastName: string } {
  const [firstName = '', ...rest] = instructor.name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

function InviteForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const invite = useMutation({
    mutationFn: (confirmPlayer: boolean) =>
      apiPost<InviteInstructorResponse>('/api/director/instructors', { name: name.trim(), email: email.trim(), ...(confirmPlayer ? { confirmPlayer } : {}) }),
    onSuccess: onDone,
  });
  /* The address is a player's login: the server names who, and she decides (2026-10-18). */
  const asking = invite.error instanceof ApiError && invite.error.code === 'NEEDS_CONFIRMATION' ? invite.error.message : null;
  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-light bg-paper p-5"
      onSubmit={(event) => { event.preventDefault(); if (!invite.isPending) invite.mutate(false); }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="instructor-name"><Input id="instructor-name" required maxLength={120} value={name} onChange={(e) => { setName(e.target.value); invite.reset(); }} autoFocus /></Field>
        <Field label="Email" htmlFor="instructor-email"><Input id="instructor-email" type="email" required value={email} onChange={(e) => { setEmail(e.target.value); invite.reset(); }} /></Field>
      </div>
      {asking ? (
        <div role="alert" className="flex flex-col gap-3 rounded-md border border-light bg-chalk p-4">
          <p className="text-[15px] text-ink">{asking}</p>
          <div className="flex flex-wrap gap-3">
            <Button type="button" disabled={invite.isPending} onClick={() => invite.mutate(true)}>{invite.isPending ? 'Sending…' : 'Make them an instructor'}</Button>
            <Button type="button" variant="secondary" disabled={invite.isPending} onClick={() => invite.reset()}>Use a different email</Button>
          </div>
        </div>
      ) : invite.error ? (
        <ErrorNote>{invite.error instanceof ApiError ? invite.error.message : 'Could not send the invitation.'}</ErrorNote>
      ) : null}
      {asking ? null : (
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={invite.isPending || !name.trim() || !email.trim()}>{invite.isPending ? 'Sending…' : 'Send invite'}</Button>
          <Button variant="secondary" disabled={invite.isPending} onClick={onCancel}>Cancel</Button>
        </div>
      )}
    </form>
  );
}
